import { format, subDays, subMonths, subYears } from "date-fns";

import { DATE_FORMAT } from "./constants";

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Returns a `{ startDate, endDate }` date range for a named relative period,
 * formatted with `dateFormat` (defaults to the ISO date-only format). Unknown
 * periods fall back to the last week.
 * @param timePeriod - one of `week` | `month` | `quarter` | `year`
 * @param dateFormat - output format for both bounds (defaults to `DATE_FORMAT`)
 */
export const getDateRange = (timePeriod: string, dateFormat: string = DATE_FORMAT): DateRange => {
  const today = new Date();
  switch (timePeriod) {
    case "month":
      return { startDate: format(subMonths(today, 1), dateFormat), endDate: format(today, dateFormat) };
    case "quarter":
      return { startDate: format(subMonths(today, 3), dateFormat), endDate: format(today, dateFormat) };
    case "year":
      return { startDate: format(subYears(today, 1), dateFormat), endDate: format(today, dateFormat) };
    case "week":
    default:
      return { startDate: format(subDays(today, 7), dateFormat), endDate: format(today, dateFormat) };
  }
};

/** Computes an ISO timestamp range from a number of days back to now. */
export function computeDateRange(days: number): { from: string; to: string } {
  const to = new Date().toISOString();
  const from = new Date(Date.now() - days * 86_400_000).toISOString();
  return { from, to };
}

/**
 * Returns the current calendar month as a half-open UTC range `[fromUtc, beforeUtc)`,
 * suitable for "this month" count filters. `fromUtc` is the first instant of the
 * month; `beforeUtc` is the first instant of the next month (exclusive).
 */
export function currentMonthRangeUtc(today: Date = new Date()): { fromUtc: string; beforeUtc: string } {
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  return {
    fromUtc: new Date(Date.UTC(year, month, 1)).toISOString(),
    beforeUtc: new Date(Date.UTC(year, month + 1, 1)).toISOString(),
  };
}

/**
 * Computes the current UTC-offset minutes for a given IANA zone name using the
 * runtime's `Intl` implementation. Returns `null` when the zone is unknown.
 *
 * Used by timezone auto-populate fallbacks so a browser reporting an IANA zone
 * that isn't in the backend seed (e.g. `Europe/London` during BST) can still
 * match against a seeded zone sharing the same current offset.
 */
export function getCurrentOffsetMinutes(iana: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: iana,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());
    const label = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    if (label === "GMT" || label === "UTC") return 0;
    const m = label.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!m) return null;
    const sign = m[1] === "+" ? 1 : -1;
    return sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
  } catch {
    return null;
  }
}
